import React, { useState, useEffect } from "react";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import { Calendar } from "react-date-range";
import { Popover } from "antd";
import { BiCalendar } from "react-icons/bi";
import { format } from "date-fns";

interface DatePickerProps {
  value?: Date | null | string;
  onChange: (date: string) => void;
  placeholder: string;
}

const DatePicker = ({ value, onChange, placeholder }: DatePickerProps) => {
  // Helper function to check if value is valid
  const isValidDate = (val: any): val is Date => {
    return val instanceof Date && !isNaN(val.getTime());
  };

  // Helper function to convert value to Date or undefined
  const getInitialDate = (val: any): Date | undefined => {
    if (!val) return undefined;
    if (val instanceof Date) return isValidDate(val) ? val : undefined;
    if (typeof val === "string") {
      const date = new Date(val);
      return isValidDate(date) ? date : undefined;
    }
    return undefined;
  };

  const initialDate = getInitialDate(value);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    initialDate
  );

  // Sync internal state with value prop
  useEffect(() => {
    const newDate = getInitialDate(value);
    setSelectedDate(newDate);
  }, [value]);

  const handleSelect = (date: Date) => {
    setSelectedDate(date);
    // Return ISO 8601 date string for API compatibility
    const isoDateString = date.toISOString();
    onChange(isoDateString);
  };

  return (
    <Popover
      content={
        <Calendar
          date={selectedDate}
          onChange={handleSelect}
          showDateDisplay={false}
        />
      }
      trigger="click"
      placement="bottom"
    >
      <button className="border border-[#D0D5DD] rounded-lg p-2 flex justify-start items-center gap-2">
        <BiCalendar strokeWidth={0.2} size={20} color="#9CA3AF" />
        {selectedDate ? (
          <p className="text-sm font-medium">
            {format(selectedDate, "dd/MM/yyyy")}
          </p>
        ) : (
          <p className="text-sm font-medium">{placeholder || "Select date"}</p>
        )}
      </button>
    </Popover>
  );
};

export default DatePicker;
